import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, Building2, CheckCircle, Edit2, MapPin, PlusCircle, Send, Sparkles, Wand2, X } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";

interface Blast {
  id: number; status: string; profile_generated: boolean;
  anonymized_profile: string; contact_count: number; sent_at: string | null;
}
interface Candidate {
  id: number; full_name: string; firm_name: string; practice_area: string;
  jd_year: number | null; location: string; candidate_type: string;
  pipeline_stage: string; resume_url: string | null;
}
interface BlastContact {
  id: number; skm_contact_id: number; firm_name: string; contact_name: string;
  contact_title: string; contact_email: string; status: string;
  follow_up_status: string; sent_at: string | null; responded_at: string | null;
  follow_up_sent_at: string | null; response_notes: string;
  can_create_submission: boolean;
}
interface EligibleFirm {
  firm_name: string; contact_preferences: string; has_contact: boolean;
  skm_contact_id: number | null; contact_name: string | null;
  contact_title: string | null; contact_email: string | null; already_added: boolean;
}
interface ExcludedFirm { firm_name: string; reason: string; }

interface Props {
  blast: Blast; candidate: Candidate; contacts: BlastContact[];
  eligible_firms: EligibleFirm[]; excluded_firms: ExcludedFirm[];
  ai_assist: boolean;
}

const TONES = ["conservative", "assertive", "narrative"] as const;
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  queued:         { label: "Queued",         className: "bg-gray-100 text-gray-500" },
  sent:           { label: "Sent",           className: "bg-blue-50 text-blue-700" },
  responded:      { label: "Responded",      className: "bg-amber-50 text-amber-700" },
  interested:     { label: "Interested",     className: "bg-green-50 text-green-700" },
  not_interested: { label: "Not Interested", className: "bg-red-50 text-red-600" },
  bounced:        { label: "Bounced",        className: "bg-red-100 text-red-700" },
};
function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function SkmBlastDetail({ blast, candidate, contacts, eligible_firms, excluded_firms, ai_assist }: Props) {
  const [tone, setTone]             = useState("conservative");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing]       = useState(false);
  const [editText, setEditText]     = useState(blast.anonymized_profile);
  const [saving, setSaving]         = useState(false);
  const [sending, setSending]       = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);

  const isSent    = blast.status === "sent";
  const isSending = blast.status === "sending";
  const isLocked  = isSent || isSending;

  function generate() {
    setGenerating(true);
    router.post(`/skm/blasts/${blast.id}/generate_profile`, { tone }, { onFinish: () => setGenerating(false) });
  }
  function saveEdit() {
    setSaving(true);
    router.patch(`/skm/blasts/${blast.id}`, { anonymized_profile: editText }, {
      onFinish: () => { setSaving(false); setEditing(false); },
    });
  }
  function addContact(contactId: number) {
    router.post(`/skm/blasts/${blast.id}/contacts`, { skm_contact_id: contactId });
  }
  function removeContact(bcId: number) {
    router.delete(`/skm/blasts/${blast.id}/contacts/${bcId}`);
  }
  function sendBlast() {
    setSending(true);
    router.post(`/skm/blasts/${blast.id}/send`, {}, { onFinish: () => setSending(false) });
  }

  const firmsWithContacts    = eligible_firms.filter((f) => f.has_contact);
  const firmsWithoutContacts = eligible_firms.filter((f) => !f.has_contact);

  return (
    <AppLayout title="SKM Blast">
      <Link href="/skm/generate" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> SKM Generator
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SKM Blast</h1>
          <div className="flex items-center gap-2 mt-1">
            <Link href={`/candidates/${candidate.id}`} className="text-sm text-gray-500 hover:text-green-700">{candidate.full_name}</Link>
            <span className="text-gray-300">·</span>
            <span className="text-sm text-gray-400">{candidate.firm_name}</span>
            {candidate.location && (
              <><span className="text-gray-300">·</span>
              <span className="text-sm text-gray-400 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{candidate.location}</span></>
            )}
          </div>
        </div>
        {isSent && (
          <span className="flex items-center gap-1 text-sm font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg">
            <CheckCircle className="w-4 h-4" /> Sent {fmt(blast.sent_at)}
          </span>
        )}
        {isSending && (
          <span className="flex items-center gap-1 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
            <Send className="w-4 h-4" /> Sending…
          </span>
        )}
      </div>

      {/* Profile section */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-800">Anonymized Profile</h2>
          {blast.profile_generated && !editing && !isLocked && (
            <Button variant="outline" size="sm" onClick={() => { setEditText(blast.anonymized_profile); setEditing(true); }}>
              <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          )}
        </div>
        {!blast.profile_generated && (
          <div>
            <p className="text-sm text-gray-500 mb-4">Generate an anonymized profile in Steven's voice. Name and firm will not appear.</p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm text-gray-600 font-medium">Tone:</span>
              {TONES.map((t) => (
                <button key={t} onClick={() => setTone(t)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium capitalize transition-colors ${
                    tone === t ? "bg-green-700 text-white border-green-700" : "border-gray-200 text-gray-500 hover:border-green-400"
                  }`}>{t}</button>
              ))}
            </div>
            {ai_assist ? (
              <Button onClick={generate} disabled={generating} className="bg-green-700 hover:bg-green-800 text-white">
                <Wand2 className="w-4 h-4 mr-1.5" />{generating ? "Generating..." : "Generate Profile"}
              </Button>
            ) : (
              <p className="text-sm text-amber-600">
                <Sparkles className="w-3.5 h-3.5 inline mr-1" />
                AI Assist required — <Link href="/settings" className="underline">enable in Settings</Link>
              </p>
            )}
          </div>
        )}
        {blast.profile_generated && !editing && (
          <div>
            <div className="bg-gray-50 rounded p-4 text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
              {blast.anonymized_profile || <span className="text-gray-300 italic">Empty profile</span>}
            </div>
            {!isLocked && ai_assist && (
              <button onClick={generate} disabled={generating}
                className="mt-3 text-xs text-purple-700 border border-purple-200 hover:bg-purple-50 rounded px-2.5 py-1 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {generating ? "Regenerating..." : "Regenerate"}
              </button>
            )}
          </div>
        )}
        {editing && (
          <div>
            <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={12}
              className="w-full border border-gray-200 rounded p-3 text-sm font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-green-500" />
            <div className="flex gap-2 mt-2">
              <Button onClick={saveEdit} disabled={saving} size="sm" className="bg-green-700 hover:bg-green-800 text-white">
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>

      {/* Firm list section */}
      <div className={`bg-white rounded-lg border border-gray-200 p-5 mb-4 ${!blast.profile_generated ? "opacity-50 pointer-events-none" : ""}`}>
        <h2 className="text-base font-semibold text-gray-800 mb-3">
          Target Firms <span className="text-gray-400 font-normal text-sm">({firmsWithContacts.length} with contacts · {firmsWithoutContacts.length} without)</span>
        </h2>

        {firmsWithContacts.length === 0 && firmsWithoutContacts.length === 0 ? (
          <p className="text-sm text-gray-400">No eligible firms found for this candidate.</p>
        ) : (
          <div className="space-y-1">
            {[...firmsWithContacts, ...firmsWithoutContacts].map((f) => {
              const addedBc = contacts.find((bc) => bc.skm_contact_id === f.skm_contact_id);
              return (
                <div key={f.firm_name} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-gray-50">
                  <Building2 className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-800">{f.firm_name}</span>
                    {f.has_contact && f.contact_name && (
                      <span className="text-xs text-gray-400 ml-2">{f.contact_name}{f.contact_title && ` · ${f.contact_title}`}</span>
                    )}
                    {!f.has_contact && <span className="text-xs text-gray-300 ml-2 italic">no contact</span>}
                    {f.contact_preferences && (
                      <span className="text-xs text-amber-600 ml-2">⚠ {f.contact_preferences}</span>
                    )}
                  </div>
                  {f.has_contact && !isLocked && (
                    addedBc ? (
                      <button onClick={() => removeContact(addedBc.id)}
                        className="flex items-center gap-0.5 text-xs text-red-500 hover:text-red-700">
                        <X className="w-3 h-3" /> Remove
                      </button>
                    ) : (
                      <button onClick={() => addContact(f.skm_contact_id!)}
                        className="text-xs text-green-700 border border-green-200 hover:bg-green-50 rounded px-2 py-0.5">
                        + Add
                      </button>
                    )
                  )}
                  {addedBc && <span className="text-xs text-green-600 font-medium">Added</span>}
                </div>
              );
            })}
          </div>
        )}

        {excluded_firms.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <Button variant="outline" size="sm" onClick={() => setShowExcluded((p) => !p)}>
              {showExcluded ? "Hide" : "Show"} {excluded_firms.length} excluded firm{excluded_firms.length !== 1 ? "s" : ""}
            </Button>
            {showExcluded && (
              <div className="mt-2 space-y-1">
                {excluded_firms.map((f) => (
                  <div key={f.firm_name} className="flex items-center gap-2 opacity-50">
                    <Building2 className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                    <span className="text-sm text-gray-500">{f.firm_name}</span>
                    <span className="text-xs text-red-500">{f.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Active contacts */}
      {contacts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
          <h2 className="text-base font-semibold text-gray-800 mb-3">
            Selected Contacts <span className="text-gray-400 font-normal text-sm">({contacts.length})</span>
          </h2>
          <div className="divide-y divide-gray-100">
            {contacts.map((bc) => {
              const cfg = STATUS_CONFIG[bc.status] ?? STATUS_CONFIG.queued;
              const isSentContact = bc.status === "sent";
              const hasResponded  = ["responded", "interested", "not_interested"].includes(bc.status);
              return (
                <div key={bc.id} className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{bc.contact_name}</p>
                      <p className="text-xs text-gray-400">{bc.firm_name}{bc.contact_title && ` · ${bc.contact_title}`}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${cfg.className}`}>{cfg.label}</span>
                    {bc.sent_at && <span className="text-xs text-gray-400">{fmt(bc.sent_at)}</span>}
                    {bc.follow_up_status === "fu_due" && (
                      <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">Follow-up due</span>
                    )}
                  </div>
                  {/* Response buttons for sent contacts */}
                  {isSentContact && !isSent && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-400">Mark:</span>
                      {[
                        { value: "responded",     label: "Responded",     cls: "bg-amber-50 text-amber-700 border-amber-200" },
                        { value: "interested",    label: "Interested",    cls: "bg-green-50 text-green-700 border-green-200" },
                        { value: "not_interested", label: "Not Interested", cls: "bg-red-50 text-red-600 border-red-200" },
                      ].map((opt) => (
                        <button key={opt.value}
                          onClick={() => router.patch(`/skm/blasts/${blast.id}/contacts/${bc.id}/status`, { status: opt.value })}
                          className={`text-xs border rounded px-2 py-0.5 hover:opacity-80 ${opt.cls}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {hasResponded && bc.response_notes === "" && !isSent && (
                    <div className="mt-1.5">
                      <input type="text" placeholder="Response notes..."
                        className="text-xs border border-gray-200 rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-green-500"
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            router.patch(`/skm/blasts/${blast.id}/contacts/${bc.id}/status`, { response_notes: e.target.value });
                          }
                        }}
                      />
                    </div>
                  )}
                  {bc.response_notes && (
                    <p className="text-xs text-gray-500 mt-1 italic">{bc.response_notes}</p>
                  )}
                  {bc.can_create_submission && (
                    <div className="mt-2">
                      <button
                        onClick={() => router.post(`/skm/blasts/${blast.id}/contacts/${bc.id}/create_submission`)}
                        className="inline-flex items-center gap-1 text-xs bg-green-700 text-white hover:bg-green-800 rounded px-2.5 py-1 font-medium"
                      >
                        <PlusCircle className="w-3 h-3" /> Create submission
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {blast.profile_generated && !isLocked && (
        <div className="flex justify-end">
          <Button onClick={sendBlast} disabled={sending} className="bg-green-700 hover:bg-green-800 text-white">
            <Send className="w-4 h-4 mr-1.5" />
            {sending ? "Sending..." : `Mark as Sent${contacts.length > 0 ? ` (${contacts.length})` : ""}`}
          </Button>
        </div>
      )}
    </AppLayout>
  );
}
